import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Barcode, CheckCircle2, Edit3, LoaderCircle, Plus, ShieldCheck, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_LABELS = {
  AMAZON_VENDOR: 'Amazon Vendor', AMAZON_SELLER: 'Amazon Seller', FLIPKART: 'Flipkart',
  FIRSTCRY: 'FirstCry', MYNTRA: 'Myntra', NYKAA: 'Nykaa', D2C_DROGO: 'D2C Drogo',
  D2C_BAYBEE: 'D2C Baybee', D2C_DOMESTICA: 'D2C Domestica', OTHERS: 'Other',
};

const emptyForm = { platform: '', identifier_type: '', identifier_value: '', label: '', is_primary: false };

const validateValue = (type, value) => {
  const clean = value.trim();
  if (!clean) return 'Identifier value is required';
  if (!/^[A-Za-z0-9][A-Za-z0-9._:/#-]*$/.test(clean)) return 'Use letters, numbers, dots, dashes, underscores, colons, slashes, or # only';
  if (type === 'ASIN' && !/^[A-Za-z0-9]{10}$/.test(clean)) return 'ASIN must be exactly 10 letters or numbers';
  if (type === 'FSN' && !/^[A-Za-z0-9_-]{6,30}$/.test(clean)) return 'FSN must be 6–30 characters';
  if (type === 'FNSKU' && !/^[A-Za-z0-9]{10,14}$/.test(clean)) return 'FNSKU must be 10–14 letters or numbers';
  if (clean.length > 80) return 'Identifier value must be 80 characters or fewer';
  return '';
};

const SaveIcon = ({ saving, editing }) => {
  if (saving) return <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />;
  if (editing) return <CheckCircle2 className="mr-2 h-4 w-4" />;
  return <Plus className="mr-2 h-4 w-4" />;
};

const saveLabel = (saving, editing) => {
  if (saving) return 'Saving…';
  return editing ? 'Save changes' : 'Add identifier';
};

export const PlatformIdentifierManagerDialog = ({ open, onOpenChange, sku, country, onUpdated }) => {
  const [platforms, setPlatforms] = useState({});
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const skuCode = sku?.sku_code;
  const loadIdentifiers = useCallback(async () => {
    if (!skuCode) return;
    setLoading(true);
    try {
      const [identifierRes, optionsRes] = await Promise.all([
        axios.get(`${API}/skus/${encodeURIComponent(skuCode)}/platform-identifiers?country=${country}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/skus/identifier-config/options`, { headers: getAuthHeaders() }),
      ]);
      const nextOptions = optionsRes.data.platforms || [];
      setPlatforms(identifierRes.data.platforms || {});
      setOptions(nextOptions);
      setForm(current => current.platform ? current : {
        ...emptyForm,
        platform: nextOptions[0]?.value || '',
        identifier_type: nextOptions[0]?.identifier_types?.[0] || '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load marketplace identifiers');
    } finally {
      setLoading(false);
    }
  }, [skuCode, country]);

  useEffect(() => {
    if (open && skuCode) loadIdentifiers();
    if (!open) {
      setEditing(null);
      setDeleting(null);
      setForm(emptyForm);
      setFormError('');
    }
  }, [open, skuCode, loadIdentifiers]);

  const identifierTypes = useMemo(
    () => options.find(option => option.value === form.platform)?.identifier_types || [],
    [options, form.platform]
  );
  const totalIdentifiers = Object.values(platforms).reduce((sum, rows) => sum + rows.length, 0);

  const selectPlatform = (platform) => {
    const types = options.find(option => option.value === platform)?.identifier_types || [];
    setForm({ ...form, platform, identifier_type: types[0] || '' });
    setFormError('');
  };

  const resetEditor = () => {
    const platform = options[0]?.value || '';
    const type = options[0]?.identifier_types?.[0] || '';
    setEditing(null);
    setForm({ ...emptyForm, platform, identifier_type: type });
    setFormError('');
  };

  const beginEdit = (platform, identifier) => {
    setEditing({ platform, id: identifier.id });
    setForm({
      platform,
      identifier_type: identifier.identifier_type,
      identifier_value: identifier.identifier_value,
      label: identifier.label || '',
      is_primary: identifier.is_primary,
    });
    setFormError('');
  };

  const saveIdentifier = async () => {
    if (!form.platform || !form.identifier_type) {
      setFormError('Choose a platform and identifier type');
      return;
    }
    const validationError = validateValue(form.identifier_type, form.identifier_value);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = { ...form, identifier_value: form.identifier_value.trim(), label: form.label.trim() };
    try {
      if (editing) {
        await axios.put(
          `${API}/skus/${encodeURIComponent(sku.sku_code)}/platform-identifiers/${editing.platform}/${editing.id}?country=${country}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Identifier updated');
      } else {
        await axios.post(
          `${API}/skus/${encodeURIComponent(sku.sku_code)}/platform-identifiers?country=${country}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Identifier added');
      }
      resetEditor();
      await loadIdentifiers();
      onUpdated?.();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to save identifier';
      setFormError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const deleteIdentifier = async () => {
    if (!deleting) return;
    try {
      await axios.delete(
        `${API}/skus/${encodeURIComponent(sku.sku_code)}/platform-identifiers/${deleting.platform}/${deleting.id}?country=${country}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Identifier deleted');
      if (editing?.id === deleting.id) resetEditor();
      setDeleting(null);
      await loadIdentifiers();
      onUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete identifier');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden p-0 gap-0" data-testid="identifier-manager-dialog">
          <DialogHeader className="border-b border-gray-200 px-6 py-5 pr-12">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Barcode className="h-5 w-5 text-blue-600" /> Marketplace identifiers
            </DialogTitle>
            <DialogDescription data-testid="identifier-manager-description">
              {sku?.sku_code} · {sku?.product_name} · {totalIdentifiers} mapped identifier{totalIdentifiers === 1 ? '' : 's'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(0,1.35fr)_minmax(300px,0.65fr)]">
            <div className="max-h-[68vh] overflow-y-auto border-b border-gray-200 p-5 md:border-b-0 md:border-r">
              {loading && (
                <div className="flex min-h-48 items-center justify-center text-sm text-gray-500" data-testid="identifier-manager-loading">
                  <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> Loading mappings…
                </div>
              )}
              {!loading && totalIdentifiers === 0 && (
                <div className="flex min-h-48 flex-col items-start justify-center border-y border-dashed border-gray-200 py-8" data-testid="identifier-empty-state">
                  <ShieldCheck className="mb-3 h-7 w-7 text-blue-600" />
                  <p className="font-semibold text-gray-900">No marketplace identifiers yet</p>
                  <p className="mt-1 max-w-md text-sm text-gray-500">Add the first marketplace code. It will automatically become primary for that platform.</p>
                </div>
              )}
              {!loading && totalIdentifiers > 0 && (
                <div className="space-y-5" data-testid="identifier-platform-groups">
                  {Object.entries(platforms).map(([platform, rows]) => (
                    <section key={platform} data-testid={`identifier-platform-${platform.toLowerCase()}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">{PLATFORM_LABELS[platform] || platform}</h3>
                        <span className="text-xs text-gray-500">{rows.length} mapping{rows.length === 1 ? '' : 's'}</span>
                      </div>
                      <div className="divide-y divide-gray-100 border-y border-gray-200">
                        {rows.map(identifier => (
                          <div key={identifier.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center" data-testid={`identifier-row-${identifier.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className="border-gray-200 bg-gray-50 text-[10px] text-gray-600">{identifier.identifier_type}</Badge>
                                {identifier.is_primary && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50" data-testid={`identifier-primary-${identifier.id}`}><Star className="mr-1 h-3 w-3 fill-current" />Primary</Badge>}
                              </div>
                              <p className="mt-1 break-all font-mono text-sm font-semibold text-gray-900" data-testid={`identifier-value-${identifier.id}`}>{identifier.identifier_value}</p>
                              {identifier.label && <p className="mt-0.5 text-xs text-gray-500">{identifier.label}</p>}
                            </div>
                            <div className="flex shrink-0 gap-1">
                              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => beginEdit(platform, identifier)} aria-label={`Edit ${identifier.identifier_value}`} data-testid={`edit-${identifier.id}-btn`}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleting({ ...identifier, platform })} aria-label={`Delete ${identifier.identifier_value}`} data-testid={`delete-${identifier.id}-btn`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>

            <div className="max-h-[68vh] overflow-y-auto bg-gray-50/70 p-5" data-testid="identifier-editor">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{editing ? 'Edit identifier' : 'Add identifier'}</p>
                  <p className="mt-0.5 text-xs text-gray-500">One primary mapping is enforced per platform.</p>
                </div>
                {editing && <Button variant="ghost" size="sm" onClick={resetEditor} data-testid="cancel-identifier-edit-button">Cancel edit</Button>}
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier-platform">Platform *</Label>
                  <Select value={form.platform} onValueChange={selectPlatform} disabled={Boolean(editing)}>
                    <SelectTrigger id="identifier-platform" data-testid="platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                    <SelectContent>{options.map(option => <SelectItem key={option.value} value={option.value} data-testid={`platform-option-${option.value.toLowerCase()}`}>{PLATFORM_LABELS[option.value] || option.value}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="identifier-type">Identifier type *</Label>
                  <Select value={form.identifier_type} onValueChange={identifier_type => setForm({ ...form, identifier_type })}>
                    <SelectTrigger id="identifier-type" data-testid="identifier-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{identifierTypes.map(type => <SelectItem key={type} value={type} data-testid={`identifier-type-option-${type.toLowerCase()}`}>{type.replaceAll('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="identifier-value">Identifier value *</Label>
                  <Input id="identifier-value" value={form.identifier_value} onChange={event => { setForm({ ...form, identifier_value: event.target.value }); setFormError(''); }} placeholder={form.identifier_type === 'ASIN' ? 'e.g. B0ABC12345' : 'Marketplace code'} maxLength={80} autoComplete="off" data-testid="identifier-value-input" />
                  <p className="text-[11px] text-gray-500">Uniqueness is checked across every Master SKU on this platform.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="identifier-label">Label <span className="font-normal text-gray-500">(optional)</span></Label>
                  <Input id="identifier-label" value={form.label} onChange={event => setForm({ ...form, label: event.target.value })} placeholder="e.g. Black / Pack of 2" maxLength={80} data-testid="identifier-label-input" />
                </div>
                <div className="flex items-center justify-between border-y border-gray-200 py-3">
                  <div>
                    <Label htmlFor="identifier-primary">Primary for this platform</Label>
                    <p className="mt-0.5 text-[11px] text-gray-500">Selecting this replaces the current primary.</p>
                  </div>
                  <Switch id="identifier-primary" checked={form.is_primary} onCheckedChange={is_primary => setForm({ ...form, is_primary })} data-testid="primary-checkbox" aria-label="Set as primary identifier" />
                </div>
                {formError && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert" data-testid="identifier-form-error">{formError}</div>}
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={saveIdentifier} disabled={saving || loading} data-testid="save-identifier-btn">
                  <SaveIcon saving={saving} editing={editing} />
                  {saveLabel(saving, editing)}
                </Button>
                <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)} data-testid="close-identifier-manager-button">Done</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleting)} onOpenChange={nextOpen => !nextOpen && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-identifier-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete marketplace identifier?</AlertDialogTitle>
            <AlertDialogDescription>This removes {deleting?.identifier_value} from {PLATFORM_LABELS[deleting?.platform] || deleting?.platform}. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-identifier-button">Keep identifier</AlertDialogCancel>
            <AlertDialogAction onClick={deleteIdentifier} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete identifier</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};